/**
 * The dairy lactation engine. Pure functions shared by the facet reducer, the server routes and the
 * web: lactation identity from calvings and starts, days in milk, daily totals, peaks, the 305-day
 * projection (always labeled calculated, with its method) and milk held out of the tank.
 *
 * Rules kept honest here:
 * - Calving day is day 0 of the lactation. DIM is a derived value with that explicit date rule.
 * - A calving only starts a lactation for a cow that has dairy records; beef cows stay invisible.
 * - Starts within 30 days of each other are one freshening (a calving plus a start, twins, a
 *   duplicate), never two lactations.
 * - A new start without a dry-off ends the previous lactation and is flagged for review.
 * - Missing readings stay missing. Estimates, lab-reported and calculated values keep their labels.
 */
import { daysBetween, type FacetEvent, type Quantity } from "../../../contract";

// ---------------------------------------------------------------- event payloads

export type Quarter = "lf" | "rf" | "lr" | "rr";
export type MilkUnit = "lb" | "kg";

export interface LactationStartData { cause?: "calving" | "abortion" | "induced" | "bought_fresh" | "other"; lactation?: number; notes?: string }
export interface MilkWeightData { milk: Quantity; period: "day" | "milking"; session?: "am" | "midday" | "pm"; milkings?: number; source?: "meter" | "robot" | "bucket" | "estimate"; notes?: string }
export interface TestDayData {
  milk?: Quantity; fat_pct?: number; protein_pct?: number; scc?: number; mun?: number; source?: "dhia" | "lab" | "on_farm"; lab?: string; sample_id?: string;
  dim_reported?: number; lactation_reported?: number; me305_reported?: Quantity; milkings?: number; notes?: string;
}
export interface DryOffData { treatment?: string; teat_sealant?: boolean; protocol?: string; planned_on?: string; reason?: "planned" | "low_milk" | "health" | "other"; notes?: string }
export interface FreshCheckData {
  temp?: Quantity; ketones?: "negative" | "trace" | "small" | "moderate" | "large"; appetite?: "good" | "fair" | "poor";
  action?: "none" | "recheck" | "treated" | "vet" | "hospital"; discharge?: "clear" | "cloudy" | "pus" | "fetid"; da?: "none" | "left" | "right";
  attitude?: "bright" | "dull" | "down"; bhb?: number; notes?: string;
}
export interface MastitisData {
  quarters?: Quarter[]; severity?: "mild" | "moderate" | "severe"; discard?: boolean; discard_until?: string; stage?: "new" | "recheck" | "cleared";
  cmt?: "negative" | "trace" | "one" | "two" | "three"; culture?: "not_sent" | "pending" | "back"; culture_result?: string; treatment?: string; recheck_on?: string; notes?: string;
}
export interface MilkDispositionData { disposition: "discarded" | "withheld" | "sold"; until?: string; reason?: "treatment" | "mastitis" | "fresh" | "high_scc" | "other"; amount?: Quantity; notes?: string }

export const DAIRY_EVENT_TYPES = [
  "dairy.lactation_start", "dairy.milk_weight", "dairy.test_day", "dairy.dry_off", "dairy.fresh_check", "dairy.mastitis", "dairy.milk_disposition",
] as const;

// ---------------------------------------------------------------- constants

/** Starts closer than this are the same freshening. */
export const SAME_FRESHENING_DAYS = 30;
/** Badges call a milking cow "fresh" through this many days in milk. */
export const FRESH_BADGE_DAYS = 21;
/** Defaults for ranch settings. */
export const DEFAULT_FRESH_DAYS = 10;
export const DEFAULT_DRY_PERIOD = 60;
export const DEFAULT_SCC_LIMIT = 200;
/** Fever and ketosis lines used to flag fresh checks. */
export const FEVER_F = 103.0;
export const FEVER_C = 39.4;
export const BHB_KETOSIS = 1.2;
/** A mastitis case with no activity for this long drops off the morning list. */
export const MASTITIS_ACTIVE_DAYS = 45;
/** Milk comparisons only: liters are converted with standard milk density. */
export const KG_PER_L = 1.03;
const KG_PER_LB = 0.45359237;

// ---------------------------------------------------------------- units and dates

export function milkIn(q: Quantity | null | undefined, unit: MilkUnit): number | null {
  if (!q || typeof q.value !== "number" || !Number.isFinite(q.value)) return null;
  let kg: number;
  switch (q.unit) {
    case "kg": kg = q.value; break;
    case "lb": kg = q.value * KG_PER_LB; break;
    case "l": kg = q.value * KG_PER_L; break;
    case "g": kg = q.value / 1000; break;
    default: return null;
  }
  return unit === "kg" ? kg : kg / KG_PER_LB;
}

export const round1 = (n: number) => Math.round(n * 10) / 10;

/** Days in milk with calving day as day 0. Null when the fresh date is unknown or in the future. */
export function dimOn(freshOn: string | null | undefined, on: string): number | null {
  if (!freshOn) return null;
  const d = daysBetween(freshOn, on);
  return d >= 0 ? d : null;
}

const dateOf = (e: { occurred_at: string | null }) => (e.occurred_at ? e.occurred_at.slice(0, 10) : null);
const num = (v: unknown): number | undefined => (typeof v === "number" && Number.isFinite(v) ? v : typeof v === "string" && v.trim() !== "" && !Number.isNaN(Number(v)) ? Number(v) : undefined);

/** Roles a calving gives the calf rather than the cow. */
const NOT_DAM = new Set(["calf", "calves", "sire", "bull", "foster", "recipient_calf"]);
export function isDamOf(e: Pick<FacetEvent, "roles">): boolean {
  return e.roles.some((r) => !NOT_DAM.has(r));
}

// ---------------------------------------------------------------- fresh checks

export type FreshFlag = "fever" | "ketosis" | "off_feed" | "discharge" | "da" | "down";

export function freshFlags(d: FreshCheckData): FreshFlag[] {
  const out: FreshFlag[] = [];
  if (d.temp && typeof d.temp.value === "number") {
    if ((d.temp.unit === "C" && d.temp.value >= FEVER_C) || (d.temp.unit !== "C" && d.temp.value >= FEVER_F)) out.push("fever");
  }
  if (d.ketones === "moderate" || d.ketones === "large" || (typeof d.bhb === "number" && d.bhb >= BHB_KETOSIS)) out.push("ketosis");
  if (d.appetite === "poor") out.push("off_feed");
  if (d.discharge === "pus" || d.discharge === "fetid") out.push("discharge");
  if (d.da === "left" || d.da === "right") out.push("da");
  if (d.attitude === "down") out.push("down");
  return out;
}

// ---------------------------------------------------------------- 305-day projection

export interface Projection {
  /** Mature-equivalent 305-day milk, calculated by Hondo. Not an official DHIA 305ME. */
  value: number;
  unit: MilkUnit;
  /** Projected 305-day milk before the lactation adjustment. */
  projected_305: number;
  factor: number;
  factor_basis: "first" | "second" | "third_plus" | "unknown";
  tests: number;
  last_dim: number;
  /** Relative daily decline used after the last test. */
  decline: number;
  decline_basis: "observed" | "assumed" | "none";
  /** Day the lactation is credited through: 305, or the dry-off day when earlier. */
  end_dim: number;
  method: "hondo-tim-1";
  calculated: true;
}

export const ASSUMED_DECLINE = 0.0015;
const MIN_DECLINE = 0.0005;
const MAX_DECLINE = 0.005;
/** A projection needs a test at least this far into the lactation. */
export const MIN_PROJECTION_DIM = 30;

export function lactationFactor(lactation: number | null | undefined): { factor: number; basis: Projection["factor_basis"] } {
  if (!lactation) return { factor: 1, basis: "unknown" };
  if (lactation === 1) return { factor: 1.25, basis: "first" };
  if (lactation === 2) return { factor: 1.1, basis: "second" };
  return { factor: 1, basis: "third_plus" };
}

/**
 * Test-interval method: the first test's yield is credited from calving, yields between tests are
 * averaged, and after the last test yield declines by the cow's own observed post-peak rate (or an
 * assumed 0.15% a day) through day 305, or the dry-off day when she was dried earlier.
 */
export function project305(points: Array<{ dim: number; milk: number }>, opts: { lactation?: number | null; end_dim?: number | null; unit: MilkUnit }): Projection | null {
  const byDim = new Map<number, number[]>();
  for (const p of points) if (p.dim >= 4 && p.dim <= 305 && p.milk > 0) byDim.set(p.dim, [...(byDim.get(p.dim) ?? []), p.milk]);
  const tests = [...byDim.entries()].map(([dim, v]) => ({ dim, milk: v.reduce((a, b) => a + b, 0) / v.length })).sort((a, b) => a.dim - b.dim);
  if (!tests.length || tests[tests.length - 1].dim < MIN_PROJECTION_DIM) return null;
  const end = Math.max(1, Math.min(305, opts.end_dim ?? 305));
  let total = 0;
  // Calving to first test.
  total += tests[0].milk * Math.min(tests[0].dim, end);
  for (let i = 0; i < tests.length - 1; i++) {
    const a = tests[i];
    const b = tests[i + 1];
    if (a.dim >= end) break;
    const stop = Math.min(b.dim, end);
    const span = stop - a.dim;
    const atStop = a.milk + ((b.milk - a.milk) * span) / (b.dim - a.dim);
    total += ((a.milk + atStop) / 2) * span;
  }
  const last = tests[tests.length - 1];
  let decline = 0;
  let basis: Projection["decline_basis"] = "none";
  if (last.dim < end) {
    const peak = tests.reduce((m, t) => (t.milk > m.milk ? t : m), tests[0]);
    if (tests.length >= 2 && last.dim > peak.dim && last.milk < peak.milk) {
      const observed = 1 - Math.pow(last.milk / peak.milk, 1 / (last.dim - peak.dim));
      decline = Math.min(MAX_DECLINE, Math.max(MIN_DECLINE, observed));
      basis = "observed";
    } else {
      decline = ASSUMED_DECLINE;
      basis = "assumed";
    }
    const days = end - last.dim;
    const k = -Math.log(1 - decline);
    total += (last.milk * (1 - Math.exp(-k * days))) / k;
  }
  const { factor, basis: factorBasis } = lactationFactor(opts.lactation);
  return {
    value: Math.round(Math.round(total) * factor), unit: opts.unit, projected_305: Math.round(total), factor, factor_basis: factorBasis, tests: tests.length,
    last_dim: last.dim, decline: Math.round(decline * 100000) / 100000, decline_basis: basis, end_dim: end, method: "hondo-tim-1", calculated: true,
  };
}

// ---------------------------------------------------------------- the lactation model

export interface TestPoint {
  event_id: string; on: string; dim: number | null; milk?: Quantity; fat_pct?: number; protein_pct?: number; scc?: number; mun?: number;
  source?: string; lab?: string; imported: boolean; dim_reported?: number; me305_reported?: Quantity;
}
export interface DailyPoint { on: string; dim: number | null; milk: Quantity; basis: "day" | "milkings"; milkings: number; partial: boolean; estimate: boolean }
export interface CheckPoint { event_id: string; on: string; dim: number | null; flags: FreshFlag[]; action?: FreshCheckData["action"]; temp?: Quantity; ketones?: string; appetite?: string }
export interface MastitisCase {
  event_id: string; on: string; quarters: Quarter[]; severity?: string; cmt?: string; culture?: string; culture_result?: string; treatment?: string;
  recheck_on?: string; discard?: boolean; discard_until?: string; status: "open" | "cleared"; cleared_on?: string; last_on: string; visits: number;
}
export interface Peak { milk: Quantity; dim: number | null; on: string; basis: "test" | "day" }
export type ReviewFlag = "no_dry_off" | "extra_dry_off" | "record_after_dry_off" | "start_unknown" | "start_estimated";
/** Flags that mean a record is probably missing or wrong, as opposed to just unknown. */
export const RECORD_PROBLEMS: ReviewFlag[] = ["no_dry_off", "extra_dry_off", "record_after_dry_off"];

export interface Lactation {
  number: number | null;
  number_basis: "recorded" | "counted" | "lab" | "unknown";
  start: string | null;
  start_basis: "calving" | "recorded" | "estimated" | "unknown";
  cause?: LactationStartData["cause"];
  start_event_ids: string[];
  dry_on: string | null;
  dry_event_id: string | null;
  planned_dry_on: string | null;
  dry_treatment?: string;
  end: string | null;
  end_reason: "dry_off" | "next_lactation" | null;
  review: ReviewFlag[];
  tests: TestPoint[];
  daily: DailyPoint[];
  checks: CheckPoint[];
  mastitis: MastitisCase[];
  peak: Peak | null;
  me305: Projection | null;
  me305_reported: { milk: Quantity; on: string } | null;
  unit: MilkUnit;
}

export interface MilkOut { since: string; until?: string; kind: "discarded" | "withheld"; reason?: string; event_id: string; from: "mastitis" | "disposition" }
export interface Disposition { event_id: string; on: string; disposition: MilkDispositionData["disposition"]; until?: string; reason?: string }

export interface DairyDetail {
  status: "milking" | "dry" | "heifer";
  lactations: Lactation[];
  milk_out: MilkOut | null;
  dispositions: Disposition[];
  /** Mastitis cases outside any lactation (heifers, dry cows). */
  other_mastitis: MastitisCase[];
  as_of: string;
}

/** The milk-out spell still running on a date, so a new record continues it rather than restarting it. */
const stillOut = (out: MilkOut | null, on: string): MilkOut | null => (out && (!out.until || out.until >= on) ? out : null);

interface StartCandidate { on: string; event_id: string; from: "calving" | "start"; cause?: LactationStartData["cause"]; number?: number }

function mainUnit(tests: TestPoint[], daily: DailyPoint[]): MilkUnit {
  let lb = 0;
  let kg = 0;
  for (const q of [...tests.map((t) => t.milk), ...daily.map((d) => d.milk)]) {
    if (!q) continue;
    if (q.unit === "lb") lb++;
    else kg++;
  }
  return kg > lb ? "kg" : "lb";
}

/** Group milk weights by day into daily totals. A sum of milkings is partial when fewer than the stated milkings a day. */
function dailyTotals(weights: Array<{ on: string; data: MilkWeightData; recorded_at: string }>, start: string | null): DailyPoint[] {
  const byDay = new Map<string, typeof weights>();
  for (const w of weights) byDay.set(w.on, [...(byDay.get(w.on) ?? []), w]);
  const out: DailyPoint[] = [];
  for (const [on, list] of [...byDay.entries()].sort(([a], [b]) => a.localeCompare(b))) {
    const whole = list.filter((w) => w.data.period === "day").sort((a, b) => a.recorded_at.localeCompare(b.recorded_at)).at(-1);
    if (whole) {
      out.push({ on, dim: dimOn(start, on), milk: whole.data.milk, basis: "day", milkings: whole.data.milkings ?? 0, partial: false, estimate: whole.data.source === "estimate" });
      continue;
    }
    // The same named milking recorded twice counts once: the later record wins.
    const named = new Map<string, (typeof list)[number]>();
    const unnamed: typeof list = [];
    for (const w of list.filter((x) => x.data.period === "milking").sort((a, b) => a.recorded_at.localeCompare(b.recorded_at))) {
      if (w.data.session) named.set(w.data.session, w);
      else unnamed.push(w);
    }
    const sessions = [...named.values(), ...unnamed];
    if (!sessions.length) continue;
    const unit: MilkUnit = sessions[0].data.milk.unit === "kg" || sessions[0].data.milk.unit === "l" ? "kg" : "lb";
    const values = sessions.map((s) => milkIn(s.data.milk, unit)).filter((v): v is number => v !== null);
    if (!values.length) continue;
    const expected = Math.max(0, ...sessions.map((s) => s.data.milkings ?? 0));
    out.push({
      on, dim: dimOn(start, on), milk: { value: round1(values.reduce((a, b) => a + b, 0)), unit }, basis: "milkings", milkings: sessions.length,
      partial: expected ? sessions.length < expected : sessions.length < 2, estimate: sessions.some((s) => s.data.source === "estimate"),
    });
  }
  return out;
}

function peakOf(tests: TestPoint[], daily: DailyPoint[], unit: MilkUnit): Peak | null {
  let best: Peak | null = null;
  let bestValue = -1;
  for (const t of tests) {
    const v = milkIn(t.milk, unit);
    if (v !== null && v > bestValue) { bestValue = v; best = { milk: { value: round1(v), unit }, dim: t.dim, on: t.on, basis: "test" }; }
  }
  for (const d of daily) {
    if (d.partial || d.estimate) continue;
    const v = milkIn(d.milk, unit);
    if (v !== null && v > bestValue) { bestValue = v; best = { milk: { value: round1(v), unit }, dim: d.dim, on: d.on, basis: "day" }; }
  }
  return best;
}

function newLactation(start: string | null, basis: Lactation["start_basis"], ids: string[], cause?: LactationStartData["cause"], number?: number): Lactation {
  return {
    number: number ?? null, number_basis: number ? "recorded" : "unknown", start, start_basis: basis, cause, start_event_ids: ids, dry_on: null, dry_event_id: null,
    planned_dry_on: null, end: null, end_reason: null, review: basis === "unknown" ? ["start_unknown"] : [], tests: [], daily: [], checks: [], mastitis: [],
    peak: null, me305: null, me305_reported: null, unit: "lb",
  };
}

/**
 * Build a cow's dairy history from her events (any order). Returns null when she has no dairy
 * records at all, so a beef cow's calvings never create a dairy facet.
 */
export function buildDairy(events: FacetEvent[], today: string): DairyDetail | null {
  const dated = events.filter((e) => dateOf(e)).sort((a, b) => dateOf(a)!.localeCompare(dateOf(b)!) || a.recorded_at.localeCompare(b.recorded_at));
  if (!dated.some((e) => e.type.startsWith("dairy."))) return null;

  // Freshenings: calvings and recorded starts, merged when close together.
  const candidates: StartCandidate[] = [];
  for (const e of dated) {
    if (e.type === "breeding.calving" && isDamOf(e)) {
      const d = (e.data ?? {}) as Record<string, unknown>;
      candidates.push({ on: dateOf(e)!, event_id: e.id, from: "calving", cause: "calving", number: num(d.parity) ?? num(d.lactation) ?? num(d.calving_number) });
    } else if (e.type === "dairy.lactation_start") {
      const d = e.data as LactationStartData;
      candidates.push({ on: dateOf(e)!, event_id: e.id, from: "start", cause: d.cause, number: num(d.lactation) });
    }
  }
  const groups: StartCandidate[][] = [];
  for (const c of candidates) {
    const last = groups.at(-1);
    if (last && daysBetween(last[0].on, c.on) <= SAME_FRESHENING_DAYS) last.push(c);
    else groups.push([c]);
  }
  const lactations: Lactation[] = groups.map((g) => {
    const calving = g.find((c) => c.from === "calving");
    const lead = calving ?? g[0];
    const number = g.map((c) => c.number).find((n) => n !== undefined);
    return newLactation(lead.on, calving ? "calving" : "recorded", g.map((c) => c.event_id), calving ? "calving" : lead.cause, number);
  });

  // Records that imply a lactation but come before any known start get an unknown-start lactation.
  const firstStart = lactations[0]?.start ?? null;
  const implies = (e: FacetEvent) => e.type === "dairy.test_day" || e.type === "dairy.milk_weight" || e.type === "dairy.fresh_check" || e.type === "dairy.dry_off";
  if (dated.some((e) => implies(e) && (!firstStart || dateOf(e)! < firstStart))) lactations.unshift(newLactation(null, "unknown", []));

  const indexFor = (on: string): number => {
    let idx = -1;
    for (let i = 0; i < lactations.length; i++) if (lactations[i].start === null || lactations[i].start! <= on) idx = i;
    return idx;
  };

  const weights = new Map<number, Array<{ on: string; data: MilkWeightData; recorded_at: string }>>();
  const dispositions: Disposition[] = [];
  const otherMastitis: MastitisCase[] = [];
  let milkOut: MilkOut | null = null;
  const caseList = (i: number) => (i >= 0 ? lactations[i].mastitis : otherMastitis);

  for (const e of dated) {
    const on = dateOf(e)!;
    const i = indexFor(on);
    const lact = i >= 0 ? lactations[i] : null;
    switch (e.type) {
      case "dairy.dry_off": {
        if (!lact) break;
        const d = e.data as DryOffData;
        if (lact.dry_on) { if (!lact.review.includes("extra_dry_off")) lact.review.push("extra_dry_off"); break; }
        lact.dry_on = on;
        lact.dry_event_id = e.id;
        lact.planned_dry_on = d.planned_on ?? null;
        lact.dry_treatment = d.treatment;
        break;
      }
      case "dairy.test_day": {
        if (!lact) break;
        const d = e.data as TestDayData;
        if (lact.dry_on && on > lact.dry_on && !lact.review.includes("record_after_dry_off")) lact.review.push("record_after_dry_off");
        lact.tests.push({
          event_id: e.id, on, dim: null, milk: d.milk, fat_pct: d.fat_pct, protein_pct: d.protein_pct, scc: d.scc, mun: d.mun, source: d.source, lab: d.lab,
          imported: e.source_kind === "import", dim_reported: d.dim_reported, me305_reported: d.me305_reported,
        });
        if (lact.number === null && typeof d.lactation_reported === "number") { lact.number = d.lactation_reported; lact.number_basis = "lab"; }
        if (d.me305_reported) lact.me305_reported = { milk: d.me305_reported, on };
        break;
      }
      case "dairy.milk_weight": {
        if (!lact) break;
        const d = e.data as MilkWeightData;
        if (!d.milk) break;
        if (lact.dry_on && on > lact.dry_on && !lact.review.includes("record_after_dry_off")) lact.review.push("record_after_dry_off");
        weights.set(i, [...(weights.get(i) ?? []), { on, data: d, recorded_at: e.recorded_at }]);
        break;
      }
      case "dairy.fresh_check": {
        if (!lact) break;
        const d = e.data as FreshCheckData;
        lact.checks.push({ event_id: e.id, on, dim: null, flags: freshFlags(d), action: d.action, temp: d.temp, ketones: d.ketones, appetite: d.appetite });
        break;
      }
      case "dairy.mastitis": {
        const d = e.data as MastitisData;
        const stage = d.stage ?? "new";
        const list = caseList(i);
        const open = [...list].reverse().find((c) => c.status === "open" && (!d.quarters?.length || !c.quarters.length || c.quarters.some((q) => d.quarters!.includes(q))));
        if (stage === "new" || !open) {
          list.push({
            event_id: e.id, on, quarters: d.quarters ?? [], severity: d.severity, cmt: d.cmt, culture: d.culture, culture_result: d.culture_result, treatment: d.treatment,
            recheck_on: d.recheck_on, discard: d.discard, discard_until: d.discard_until, status: stage === "cleared" ? "cleared" : "open",
            cleared_on: stage === "cleared" ? on : undefined, last_on: on, visits: 1,
          });
        } else {
          open.last_on = on;
          open.visits++;
          if (d.severity) open.severity = d.severity;
          if (d.cmt) open.cmt = d.cmt;
          if (d.culture) open.culture = d.culture;
          if (d.culture_result) open.culture_result = d.culture_result;
          if (d.treatment) open.treatment = d.treatment;
          open.recheck_on = d.recheck_on;
          if (d.quarters?.length) open.quarters = [...new Set([...open.quarters, ...d.quarters])];
          if (d.discard !== undefined) open.discard = d.discard;
          if (d.discard_until) open.discard_until = d.discard_until;
          if (stage === "cleared") { open.status = "cleared"; open.cleared_on = on; }
        }
        if (d.discard) {
          const prev = stillOut(milkOut as MilkOut | null, on);
          milkOut = {
            since: prev ? prev.since : on, until: d.discard_until ?? prev?.until, kind: "discarded",
            reason: "mastitis", event_id: e.id, from: "mastitis",
          };
        }
        break;
      }
      case "dairy.milk_disposition": {
        const d = e.data as MilkDispositionData;
        dispositions.push({ event_id: e.id, on, disposition: d.disposition, until: d.until, reason: d.reason });
        if (d.disposition === "sold") milkOut = null;
        else {
          const prev = stillOut(milkOut as MilkOut | null, on);
          milkOut = { since: prev ? prev.since : on, until: d.until, kind: d.disposition, reason: d.reason, event_id: e.id, from: "disposition" };
        }
        break;
      }
    }
  }

  // Ends, estimated starts, numbers, curves and projections.
  for (let i = 0; i < lactations.length; i++) {
    const lact = lactations[i];
    const next = lactations[i + 1];
    if (lact.dry_on) { lact.end = lact.dry_on; lact.end_reason = "dry_off"; }
    else if (next?.start) { lact.end = next.start; lact.end_reason = "next_lactation"; lact.review.push("no_dry_off"); }
    if (lact.start === null) {
      const withDim = lact.tests.find((t) => typeof t.dim_reported === "number");
      if (withDim) {
        const d = new Date(`${withDim.on}T12:00:00Z`);
        d.setUTCDate(d.getUTCDate() - withDim.dim_reported!);
        lact.start = d.toISOString().slice(0, 10);
        lact.start_basis = "estimated";
        lact.review = lact.review.filter((r) => r !== "start_unknown");
        lact.review.push("start_estimated");
      }
    }
    for (const t of lact.tests) t.dim = dimOn(lact.start, t.on) ?? t.dim_reported ?? null;
    for (const c of lact.checks) c.dim = dimOn(lact.start, c.on);
    lact.daily = dailyTotals(weights.get(i) ?? [], lact.start);
    lact.unit = mainUnit(lact.tests, lact.daily);
    lact.peak = peakOf(lact.tests, lact.daily, lact.unit);
  }
  const firstKnown = lactations.findIndex((l) => l.number !== null);
  if (firstKnown === -1) lactations.forEach((l, i) => { l.number = i + 1; l.number_basis = "counted"; });
  else {
    for (let i = firstKnown - 1; i >= 0; i--) {
      const n = lactations[i + 1].number! - 1;
      lactations[i].number = n >= 1 ? n : null;
      lactations[i].number_basis = n >= 1 ? "counted" : "unknown";
    }
    for (let i = firstKnown + 1; i < lactations.length; i++) {
      if (lactations[i].number !== null && lactations[i].number! > lactations[i - 1].number!) continue;
      lactations[i].number = (lactations[i - 1].number ?? 0) + 1;
      lactations[i].number_basis = "counted";
    }
  }
  for (const lact of lactations) {
    if (lact.start === null) continue;
    const points = lact.tests.filter((t) => t.dim !== null).map((t) => ({ dim: t.dim!, milk: milkIn(t.milk, lact.unit) ?? 0 })).filter((p) => p.milk > 0);
    const endDim = lact.dry_on ? dimOn(lact.start, lact.dry_on) : null;
    lact.me305 = project305(points, { lactation: lact.number, end_dim: endDim, unit: lact.unit });
  }

  const last = lactations.at(-1);
  return {
    status: !last ? "heifer" : last.dry_on ? "dry" : "milking",
    lactations, milk_out: milkOut, dispositions, other_mastitis: otherMastitis, as_of: today,
  };
}

// ---------------------------------------------------------------- the stored facet

export interface LactationSummary {
  number: number | null; number_basis: Lactation["number_basis"]; start: string | null; start_basis: Lactation["start_basis"]; dry_on: string | null;
  end: string | null; end_reason: Lactation["end_reason"]; tests: number; peak: Peak | null; me305: number | null; unit: MilkUnit; review: ReviewFlag[];
}

export interface TestSummary {
  on: string; dim: number | null; milk?: Quantity; fat_pct?: number; protein_pct?: number; scc?: number; mun?: number; imported: boolean; event_id: string;
  /** SCC on the test before this one, to tell a new high from a chronic one. */
  previous_scc?: number;
}

/** `animal.facets.dairy`. Shared keys: lactation, dim (as of `as_of`), status. */
export interface DairyFacet {
  status: "milking" | "dry" | "heifer";
  lactation?: number;
  lactation_basis?: Lactation["number_basis"];
  /** Days in milk on `as_of`. Use dimOn(fresh_on, today) for a live value. */
  dim?: number;
  as_of: string;
  fresh_on?: string;
  fresh_basis?: Lactation["start_basis"];
  dry_on?: string;
  last_test?: TestSummary;
  last_milk?: { on: string; milk: Quantity; basis: DailyPoint["basis"]; partial: boolean };
  peak?: Peak;
  me305?: Projection;
  me305_reported?: { milk: Quantity; on: string };
  milk_out?: MilkOut;
  mastitis_open?: Array<{ on: string; last_on: string; quarters: Quarter[]; severity?: string; recheck_on?: string; event_id: string }>;
  mastitis_cases?: number;
  last_check?: { on: string; flags: FreshFlag[]; action?: FreshCheckData["action"]; event_id: string };
  lactations: LactationSummary[];
  review?: ReviewFlag[];
}

export function toFacet(detail: DairyDetail, today: string): DairyFacet {
  const cur = detail.lactations.at(-1);
  const allTests = detail.lactations.flatMap((l) => l.tests);
  const lastTest = allTests.at(-1);
  const lastDaily = cur?.daily.at(-1);
  const openCases = [...detail.lactations.flatMap((l) => l.mastitis), ...detail.other_mastitis].filter((c) => c.status === "open");
  const lastCheck = cur?.checks.at(-1);
  const facet: DairyFacet = {
    status: detail.status,
    as_of: today,
    lactations: detail.lactations.map((l) => ({
      number: l.number, number_basis: l.number_basis, start: l.start, start_basis: l.start_basis, dry_on: l.dry_on, end: l.end, end_reason: l.end_reason,
      tests: l.tests.length, peak: l.peak, me305: l.me305?.value ?? null, unit: l.unit, review: l.review,
    })),
  };
  if (cur) {
    if (cur.number !== null) { facet.lactation = cur.number; facet.lactation_basis = cur.number_basis; }
    if (cur.start) { facet.fresh_on = cur.start; facet.fresh_basis = cur.start_basis; }
    if (detail.status === "milking") { const dim = dimOn(cur.start, today); if (dim !== null) facet.dim = dim; }
    if (cur.dry_on) facet.dry_on = cur.dry_on;
    if (cur.peak) facet.peak = cur.peak;
    if (cur.me305) facet.me305 = cur.me305;
    if (cur.me305_reported) facet.me305_reported = cur.me305_reported;
    if (lastDaily) facet.last_milk = { on: lastDaily.on, milk: lastDaily.milk, basis: lastDaily.basis, partial: lastDaily.partial };
    if (lastCheck) facet.last_check = { on: lastCheck.on, flags: lastCheck.flags, action: lastCheck.action, event_id: lastCheck.event_id };
    facet.mastitis_cases = cur.mastitis.length;
    // Things a person should look at in the last two lactations (a missing dry-off shows on the earlier one).
    const review = [...new Set(detail.lactations.slice(-2).flatMap((l) => l.review))].filter((r) => RECORD_PROBLEMS.includes(r));
    if (review.length) facet.review = review;
  }
  if (lastTest) {
    const previous = allTests.slice(0, -1).reverse().find((t) => typeof t.scc === "number" && t.on < lastTest.on);
    facet.last_test = {
      on: lastTest.on, dim: lastTest.dim, milk: lastTest.milk, fat_pct: lastTest.fat_pct, protein_pct: lastTest.protein_pct, scc: lastTest.scc, mun: lastTest.mun,
      imported: lastTest.imported, event_id: lastTest.event_id, previous_scc: previous?.scc,
    };
  }
  if (detail.milk_out) facet.milk_out = detail.milk_out;
  if (openCases.length) facet.mastitis_open = openCases.map((c) => ({ on: c.on, last_on: c.last_on, quarters: c.quarters, severity: c.severity, recheck_on: c.recheck_on, event_id: c.event_id }));
  return JSON.parse(JSON.stringify(facet)) as DairyFacet;
}

// ---------------------------------------------------------------- read-time helpers

/** Live days in milk for a milking cow. */
export function liveDim(f: Pick<DairyFacet, "status" | "fresh_on"> | null | undefined, today: string): number | null {
  if (!f || f.status !== "milking") return null;
  return dimOn(f.fresh_on, today);
}

/** Milk held out of the tank by a dairy record, still in effect today. */
export function milkOutActive(f: Pick<DairyFacet, "milk_out" | "status"> | null | undefined, today: string): MilkOut | null {
  const out = f?.milk_out;
  if (!out || f?.status === "dry") return null;
  if (out.until && out.until < today) return null;
  return out;
}

export function activeMastitis(f: Pick<DairyFacet, "mastitis_open"> | null | undefined, today: string) {
  return (f?.mastitis_open ?? []).filter((c) => daysBetween(c.last_on, today) <= MASTITIS_ACTIVE_DAYS);
}

/** Health module milk holds (status only; health owns them), still in effect on a date. */
export function healthMilkHolds(health: unknown, on: string): Array<{ until: string; product: string; event_id: string }> {
  const holds = (health as { holds?: unknown } | null | undefined)?.holds;
  if (!Array.isArray(holds)) return [];
  return holds.filter((h): h is { kind: string; until: string; product: string; event_id: string } =>
    !!h && typeof h === "object" && (h as { kind?: unknown }).kind === "milk" && typeof (h as { until?: unknown }).until === "string" && (h as { until: string }).until >= on)
    .map((h) => ({ until: h.until, product: typeof h.product === "string" ? h.product : "", event_id: typeof h.event_id === "string" ? h.event_id : "" }));
}

/** Expected calving date from the breeding facet, when present. */
export function dueDate(breeding: unknown): string | null {
  const d = (breeding as { due_date?: unknown } | null | undefined)?.due_date;
  return typeof d === "string" && /^\d{4}-\d{2}-\d{2}/.test(d) ? d.slice(0, 10) : null;
}
